import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Divider, 
  Button, 
  CircularProgress, 
  Alert, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon,
  Chip
} from '@mui/material';
import { 
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ShoppingCart as ShoppingCartIcon,
  RestaurantMenu as ConsumeIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import PredictionService from '../../services/PredictionService';

const HistoryPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
}));

const HistoryChip = styled(Chip)(({ theme }) => ({
  fontWeight: 500,
  fontSize: '0.75rem',
  marginRight: theme.spacing(1),
}));

function ConsumptionHistory({ itemId }) {
  const [consumptionHistory, setConsumptionHistory] = useState([]);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    if (itemId) {
      // Define fetchHistory inside useEffect to avoid dependency issues
      const fetchHistory = async () => {
        try {
          setIsLoading(true);
          setError('');
          
          // Fetch consumption history
          const consumptionData = await PredictionService.getConsumptionHistory(itemId);
          if (consumptionData.consumption_history) {
            setConsumptionHistory(consumptionData.consumption_history);
          }
          
          // Fetch purchase history
          const purchaseData = await PredictionService.getPurchaseHistory(itemId);
          if (purchaseData.purchase_history) {
            setPurchaseHistory(purchaseData.purchase_history);
          }
        } catch (error) {
          console.error('Error fetching history:', error);
          setError('Failed to load history data. Please try again later.');
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchHistory();
    }
  }, [itemId, refreshTrigger]);

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  };

  // Combine and sort both histories by date
  const combinedHistory = [
    ...consumptionHistory.map(item => ({
      ...item,
      type: 'consumption'
    })),
    ...purchaseHistory.map(item => ({
      ...item,
      type: 'purchase'
    }))
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  // Limit to most recent 5 entries when not expanded
  const displayHistory = expanded ? combinedHistory : combinedHistory.slice(0, 5);
  const hasMoreEntries = combinedHistory.length > 5;

  if (isLoading) {
    return (
      <HistoryPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">
            Usage History
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={40} />
        </Box>
      </HistoryPaper>
    );
  }

  if (error) {
    return (
      <HistoryPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">
            Usage History
          </Typography>
        </Box>
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </HistoryPaper>
    );
  }

  if (combinedHistory.length === 0) {
    return (
      <HistoryPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">
            Usage History
          </Typography>
        </Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          No usage history recorded yet.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          As you record consumption and purchases, they will appear here.
        </Typography>
      </HistoryPaper>
    );
  }

  return (
    <HistoryPaper>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">
            Usage History
          </Typography>
        </Box>
        <Button 
          variant="outlined" 
          size="small"
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Box>

      <List disablePadding>
        {displayHistory.map((entry, index) => (
          <React.Fragment key={`${entry.type}-${entry.date}`}>
            {index > 0 && <Divider component="li" />}
            <ListItem alignItems="flex-start">
              <ListItemIcon sx={{ minWidth: 40 }}>
                {entry.type === 'consumption' ? (
                  <ConsumeIcon color="error" />
                ) : (
                  <ShoppingCartIcon color="success" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    {entry.type === 'consumption' ? (
                      <>
                        <HistoryChip 
                          label="Consumed" 
                          size="small" 
                          color="error" 
                          variant="outlined" 
                        />
                        <Typography component="span" variant="body2">
                          {entry.consumed_quantity} units
                        </Typography>
                      </>
                    ) : (
                      <>
                        <HistoryChip 
                          label="Purchased" 
                          size="small" 
                          color="success" 
                          variant="outlined" 
                        />
                        <Typography component="span" variant="body2">
                          {entry.quantity_added} units
                        </Typography>
                      </>
                    )}
                  </Box>
                }
                secondary={
                  <>
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.primary"
                    >
                      {formatDate(entry.date)}
                    </Typography>
                    {entry.type === 'consumption' ? (
                      <Typography component="span" variant="body2" sx={{ display: 'block' }}>
                        {entry.previous_quantity} → {entry.new_quantity} units
                      </Typography>
                    ) : entry.source ? (
                      <Typography component="span" variant="body2" sx={{ display: 'block' }}>
                        Source: {entry.source}
                      </Typography>
                    ) : null}
                    {entry.notes && (
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                        "{entry.notes}"
                      </Typography>
                    )}
                  </>
                }
              />
            </ListItem>
          </React.Fragment>
        ))}
      </List>

      {hasMoreEntries && (
        <Button
          fullWidth
          onClick={toggleExpanded}
          startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ mt: 2 }}
        >
          {expanded ? 'Show Less' : `Show ${combinedHistory.length - 5} More`}
        </Button>
      )}
    </HistoryPaper>
  );
}

export default ConsumptionHistory;